import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { useAuth, API, authFetch } from "@/App";
import { toast } from "sonner";

import { 
  ArrowLeft, Clock, Save, Plus, Trash2, 
  Briefcase, Moon, Dumbbell, Users, BookOpen,
  Palette, UtensilsCrossed, Car, ShoppingCart, Timer, AlertCircle, CheckCircle,
  Sparkles, Loader2, Target, Zap, BarChart3
} from "lucide-react";

const TOTAL_WEEKLY_HOURS = 168;

const DEFAULT_CATEGORIES = [
  { id: "work_hours", name: "Work", icon: Briefcase, color: "bg-blue-500", defaultHours: 37.5 },
  { id: "sleep_hours", name: "Sleep", icon: Moon, color: "bg-purple-500", defaultHours: 56 },
  { id: "exercise_hours", name: "Exercise", icon: Dumbbell, color: "bg-green-500", defaultHours: 5 },
  { id: "family_time_hours", name: "Family/Social", icon: Users, color: "bg-pink-500", defaultHours: 20 },
  { id: "personal_development_hours", name: "Personal Development", icon: BookOpen, color: "bg-amber-500", defaultHours: 5 },
  { id: "hobbies_hours", name: "Hobbies", icon: Palette, color: "bg-cyan-500", defaultHours: 5 },
  { id: "meals_self_care_hours", name: "Meals & Self-Care", icon: UtensilsCrossed, color: "bg-orange-500", defaultHours: 14 },
  { id: "commute_hours", name: "Commute", icon: Car, color: "bg-gray-500", defaultHours: 5 },
  { id: "errands_chores_hours", name: "Errands & Chores", icon: ShoppingCart, color: "bg-rose-500", defaultHours: 5 },
  { id: "buffer_hours", name: "Buffer Time", icon: Timer, color: "bg-neutral-500", defaultHours: 5 }
];

export const TimeBudgetPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [budget, setBudget] = useState({});
  const [customCategories, setCustomCategories] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [notes, setNotes] = useState("");
  const [aiRecommendations, setAiRecommendations] = useState(null);
  const [newPriority, setNewPriority] = useState("");

  useEffect(() => {
    fetchTimeBudget();
  }, []);

  const fetchTimeBudget = async () => {
    try {
      const response = await authFetch(`${API}/time-budget`, {  });
      if (response.ok) {
        const data = await response.json();
        setBudget(data);
        setCustomCategories(data.categories || []);
        setPriorities(data.priorities || []);
        setNotes(data.notes || "");
        setAiRecommendations(data.ai_recommendations || null);
      }
    } catch (_error) {
      console.error("Error fetching time budget:", _error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = () => {
    let total = 0;
    DEFAULT_CATEGORIES.forEach(cat => {
      total += parseFloat(budget[cat.id] || cat.defaultHours) || 0;
    });
    customCategories.forEach(cat => {
      total += parseFloat(cat.hours) || 0;
    });
    return total;
  };

  const remaining = TOTAL_WEEKLY_HOURS - calculateTotal();
  const percentageUsed = (calculateTotal() / TOTAL_WEEKLY_HOURS) * 100;

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...budget,
        categories: customCategories,
        priorities,
        notes,
        ai_recommendations: aiRecommendations
      };
      
      const response = await authFetch(`${API}/time-budget`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success("Time budget saved!");
      } else {
        toast.error("Failed to save time budget");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateAI = async () => {
    setGeneratingAI(true);
    try {
      const currentBudget = {
        categories: DEFAULT_CATEGORIES.map(cat => ({
          name: cat.name,
          hours: parseFloat(budget[cat.id] || cat.defaultHours) || 0
        })),
        custom_categories: customCategories,
        total_hours: calculateTotal(),
        remaining_hours: remaining,
        priorities,
        notes
      };

      const response = await authFetch(`${API}/time-budget/ai-recommendations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify(currentBudget)
      });

      if (response.ok) {
        const data = await response.json();
        setAiRecommendations(data.recommendations);
        toast.success("AI recommendations generated!");
      } else {
        toast.error("Failed to generate recommendations");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setGeneratingAI(false);
    }
  };

  const addCustomCategory = () => {
    setCustomCategories([...customCategories, { name: "", hours: 0 }]);
  };

  const removeCustomCategory = (index) => {
    setCustomCategories(customCategories.filter((_, i) => i !== index));
  };

  const updateCustomCategory = (index, field, value) => {
    const updated = [...customCategories];
    updated[index][field] = field === "hours" ? parseFloat(value) || 0 : value;
    setCustomCategories(updated);
  };

  const addPriority = () => {
    if (newPriority.trim()) {
      setPriorities([...priorities, newPriority.trim()]);
      setNewPriority("");
    }
  };

  const removePriority = (index) => {
    setPriorities(priorities.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-neutral-600">Loading time budget...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="time-budget-page">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} className="text-neutral-600">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Dashboard
              </Button>
              <div>
                <h1 className="text-xl font-semibold text-neutral-900">Time Budget</h1>
                <p className="text-sm text-neutral-500">Manage your 168 weekly hours wisely</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={() => navigate("/time-budget/actuals")}
                className="border-purple-300 text-purple-600 hover:bg-purple-50"
                data-testid="track-actuals-btn"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Track Actuals
              </Button>
              <Button
                variant="outline"
                onClick={handleGenerateAI}
                disabled={generatingAI}
                className="border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14]/10"
                data-testid="generate-ai-btn"
              >
                {generatingAI ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                AI Tips
              </Button>
              <Button onClick={handleSave} disabled={saving} className="bg-[#333333] hover:bg-[#444444] text-white" data-testid="save-budget-btn">
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Saving..." : "Save Budget"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* What is Time Budget - Explanation Card */}
        <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 mb-6">
          <CardContent className="p-6">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-blue-900 mb-2">What is a Weekly Time Budget?</h3>
                <p className="text-blue-800 mb-4">
                  Everyone has exactly <strong>168 hours</strong> each week. A time budget helps you intentionally allocate 
                  these hours across work, rest, relationships, and personal growth—just like a financial budget helps 
                  you manage money.
                </p>
                
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div className="bg-white/60 rounded-lg p-3 border border-blue-100">
                    <h4 className="font-medium text-blue-900 mb-1 flex items-center">
                      <Target className="w-4 h-4 mr-2 text-blue-600" />
                      How It Works
                    </h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• Set target hours for each life category</li>
                      <li>• Track actual time against your plan</li>
                      <li>• Get AI recommendations for optimization</li>
                      <li>• Adjust weekly based on changing priorities</li>
                    </ul>
                  </div>
                  <div className="bg-white/60 rounded-lg p-3 border border-blue-100">
                    <h4 className="font-medium text-blue-900 mb-1 flex items-center">
                      <Zap className="w-4 h-4 mr-2 text-green-600" />
                      Benefits of Sticking to Your Budget
                    </h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• Reduced stress from overcommitment</li>
                      <li>• Better work-life balance</li>
                      <li>• More time for what truly matters</li>
                      <li>• Increased productivity and focus</li>
                    </ul>
                  </div>
                </div>
                
                <p className="text-sm text-blue-600 italic">
                  💡 Tip: Start with realistic estimates. It's better to have buffer time than to over-schedule yourself.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Overview Card */}
        <Card className="border-neutral-200 mb-8">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-center md:text-left">
                <h2 className="text-2xl font-bold text-neutral-900 mb-1">Weekly Time Budget</h2>
                <p className="text-neutral-500">
                  You have {TOTAL_WEEKLY_HOURS} hours each week. Allocate them wisely to achieve balance.
                </p>
              </div>
              <div className="text-center">
                <div className={`text-4xl font-bold ${remaining >= 0 ? "text-green-600" : "text-red-600"}`}>
                  {remaining.toFixed(1)}
                </div>
                <p className="text-sm text-neutral-500">Hours {remaining >= 0 ? "Available" : "Over Budget"}</p>
              </div>
            </div>
            <div className="mt-6">
              <div className="flex justify-between text-sm text-neutral-600 mb-2">
                <span>Allocated: {calculateTotal().toFixed(1)} hours</span>
                <span>{percentageUsed.toFixed(0)}%</span>
              </div>
              <Progress 
                value={Math.min(percentageUsed, 100)} 
                className={`h-3 ${percentageUsed > 100 ? "[&>div]:bg-red-500" : "[&>div]:bg-green-500"}`}
              />
            </div>
            {remaining < 0 && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <span className="text-red-700 text-sm">You've allocated more than 168 hours. Adjust your budget to be realistic.</span>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Main Categories */}
          <div className="md:col-span-2 space-y-6">
            <Card className="border-neutral-200">
              <CardHeader>
                <CardTitle className="text-lg">Time Allocation</CardTitle>
                <CardDescription>Adjust hours for each category</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {DEFAULT_CATEGORIES.map((cat) => {
                  const Icon = cat.icon;
                  const hours = budget[cat.id] ?? cat.defaultHours;
                  return (
                    <div key={cat.id} className="flex items-center space-x-4">
                      <div className={`w-10 h-10 rounded-lg ${cat.color} flex items-center justify-center`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <Label className="text-sm font-medium">{cat.name}</Label>
                        <div className="flex items-center space-x-2 mt-1">
                          <Input
                            type="number"
                            step="0.5"
                            min="0"
                            max="168"
                            value={hours}
                            onChange={(e) => setBudget({ ...budget, [cat.id]: parseFloat(e.target.value) || 0 })}
                            className="w-24 h-8"
                            data-testid={`input-${cat.id}`}
                          />
                          <span className="text-sm text-neutral-500">hrs/week</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-sm text-neutral-400">{((hours / TOTAL_WEEKLY_HOURS) * 100).toFixed(0)}%</span>
                      </div>
                    </div>
                  );
                })}

                {/* Custom Categories */}
                {customCategories.length > 0 && (
                  <div className="pt-4 border-t">
                    <p className="text-sm font-medium text-neutral-700 mb-3">Custom Categories</p>
                    {customCategories.map((cat, idx) => (
                      <div key={idx} className="flex items-center space-x-4 mb-3">
                        <Input
                          placeholder="Category name"
                          value={cat.name}
                          onChange={(e) => updateCustomCategory(idx, "name", e.target.value)}
                          className="flex-1 h-8"
                        />
                        <Input
                          type="number"
                          step="0.5"
                          min="0"
                          value={cat.hours}
                          onChange={(e) => updateCustomCategory(idx, "hours", e.target.value)}
                          className="w-20 h-8"
                        />
                        <Button variant="ghost" size="sm" onClick={() => removeCustomCategory(idx)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                <Button variant="outline" size="sm" onClick={addCustomCategory} className="mt-2">
                  <Plus className="w-4 h-4 mr-1" />
                  Add Custom Category
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Priorities */}
            <Card className="border-neutral-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Weekly Priorities</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 mb-4">
                  {priorities.map((priority, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-neutral-50 rounded-lg">
                      <span className="text-sm text-neutral-700">{priority}</span>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => removePriority(idx)}>
                        <Trash2 className="w-3 h-3 text-neutral-400" />
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="flex space-x-2">
                  <Input
                    placeholder="Add priority..."
                    value={newPriority}
                    onChange={(e) => setNewPriority(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && addPriority()}
                    className="h-8 text-sm"
                  />
                  <Button variant="outline" size="sm" onClick={addPriority}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* AI Recommendations */}
            <Card className={`border-2 ${aiRecommendations ? 'border-[#39FF14]/30 bg-[#39FF14]/5' : 'border-neutral-200'}`}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center">
                  <Sparkles className="w-4 h-4 mr-2 text-[#39FF14]" />
                  AI Recommendations
                </CardTitle>
              </CardHeader>
              <CardContent>
                {aiRecommendations ? (
                  <div className="space-y-4">
                    {aiRecommendations.summary && (
                      <p className="text-sm text-neutral-700">{aiRecommendations.summary}</p>
                    )}
                    {aiRecommendations.suggestions && aiRecommendations.suggestions.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-neutral-500 uppercase">Suggestions</p>
                        {aiRecommendations.suggestions.map((suggestion, idx) => (
                          <div key={idx} className="flex items-start space-x-2 text-sm">
                            <CheckCircle className="w-4 h-4 text-[#39FF14] flex-shrink-0 mt-0.5" />
                            <span className="text-neutral-600">{suggestion}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {aiRecommendations.warnings && aiRecommendations.warnings.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-amber-600 uppercase">Watch Out</p>
                        {aiRecommendations.warnings.map((warning, idx) => (
                          <div key={idx} className="flex items-start space-x-2 text-sm">
                            <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                            <span className="text-neutral-600">{warning}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {aiRecommendations.quick_wins && aiRecommendations.quick_wins.length > 0 && (
                      <div className="space-y-2 p-3 bg-blue-50 rounded-lg">
                        <p className="text-xs font-medium text-blue-600 uppercase flex items-center">
                          <Zap className="w-3 h-3 mr-1" />
                          Quick Wins
                        </p>
                        {aiRecommendations.quick_wins.map((win, idx) => (
                          <p key={idx} className="text-sm text-blue-700">• {win}</p>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <Sparkles className="w-8 h-8 text-neutral-300 mx-auto mb-2" />
                    <p className="text-sm text-neutral-500 mb-3">
                      Get personalized time management advice
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleGenerateAI}
                      disabled={generatingAI}
                      className="border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14]/10"
                    >
                      {generatingAI ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                      Generate
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Notes */}
            <Card className="border-neutral-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Add notes about your time management goals..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="min-h-[100px] text-sm"
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimeBudgetPage;
